/**
 * SentryConnectModal — SELF-HOSTED Sentry setup (base URL + Auth Token + org
 * slug). This is the manual alternative to the SaaS OAuth "Connect" button on
 * the Sentry card: self-hosted Sentry has no shared OAuth app, so the user
 * authenticates with an Internal Integration / User Auth Token (a PAT) against
 * their own server.
 *
 * The user provides THREE values:
 *   - Base URL          — their Sentry server, e.g. https://sentry.example.com
 *   - Auth Token (PAT)  — Sentry → Settings → Auth Tokens (or an Internal
 *                         Integration token) with project/event read scopes
 *   - Organization slug — the org to pull issues from. On self-hosted Sentry
 *                         the default org slug is literally "sentry".
 *
 * Backend validates the token against the base URL + org slug before
 * persisting (POST /sentry/connect → { connected, organizationSlug,
 * organizationName, baseUrl } on success, or 400/401 + { error }). The
 * structured error is surfaced inline on err.message. Reuses the shared
 * .lark-*-modal CSS (same as CircleCIConnectModal) so styling matches the
 * other paste-token connect modals exactly.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectSentry } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function SentryConnectModal({ isOpen, onClose, onConnected }) {
  const [baseUrl, setBaseUrl] = useState('');
  const [authToken, setAuthToken] = useState('');
  // Self-hosted Sentry's default organization slug is literally "sentry".
  const [organizationSlug, setOrganizationSlug] = useState('sentry');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectSentry();

  useEffect(() => {
    if (isOpen) {
      setBaseUrl('');
      setAuthToken('');
      setOrganizationSlug('sentry');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const canSubmit = baseUrl.trim() && authToken.trim() && organizationSlug.trim();

  const handleSubmit = () => {
    if (!canSubmit) {
      setError('Base URL, Auth Token, and Organization slug are all required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      {
        baseUrl: baseUrl.trim(),
        authToken: authToken.trim(),
        organizationSlug: organizationSlug.trim(),
      },
      {
        onSuccess: () => {
          setConnecting(false);
          setAuthToken('');
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Sentry. Please check your URL, token, and org slug.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect self-hosted Sentry"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Sentry · Self-Hosted</h2>
        <p className="lark-modal-description">
          For a self-hosted Sentry, create an Auth Token in{' '}
          <strong>Sentry → Settings → Auth Tokens</strong> (or an Internal
          Integration) with project/event read scopes, then enter your server
          URL and organization slug below. On self-hosted Sentry the default
          organization slug is <code>sentry</code>.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="sentry-base-url">Base URL</label>
          <input
            id="sentry-base-url"
            type="text"
            value={baseUrl}
            placeholder="https://sentry.example.com"
            onChange={(e) => { setBaseUrl(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="sentry-auth-token">Auth Token</label>
          <input
            id="sentry-auth-token"
            type="password"
            value={authToken}
            placeholder="sntrys_..."
            onChange={(e) => { setAuthToken(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="sentry-org-slug">Organization slug</label>
          <input
            id="sentry-org-slug"
            type="text"
            value={organizationSlug}
            placeholder="sentry"
            onChange={(e) => { setOrganizationSlug(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !canSubmit}
            aria-label="Submit self-hosted Sentry credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Sentry connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
